import React, {useState, useEffect} from 'react';
import Moment from 'moment';
import {createRefetchContainer, graphql} from 'react-relay';
import {injectIntl} from 'react-intl';
import {withRouter} from 'react-router-dom';
import {PageWrapper} from '../../../forecast-app/my-work-tab/my-timesheets-page/timesheets_styled';
import {handleAdjacentPeriodClick, TIME_PERIOD, handleDateRangeClick} from '../../../forecast-app/my-work-tab/my-timesheets-page/timesheets_change_date_view';
import {withSocketHandling} from '../../../socket/withSocketHandling';
import {getSocketConfig} from '../../../forecast-app/my-work-tab/my-timesheets-page/timesheet_socket_handling';
import * as tracking from '../../../tracking';

import TimesheetsTeamPage from './timesheets_team_page';
import Util from '../../../forecast-app/shared/util/util';
import TrackPersonEventMutation from '../../../mutations/track_person_event_mutation';
import {dispatch, EVENT_ID} from '../../../containers/event_manager';

const timesheetsTeam = ({intl, viewer, relay, children, setSocketConfig, setSocketVariables, history}) => {
	if (!viewer.company) return <div></div>;
	if (!Util.AuthorizeViewerAccess('team-time')) {
		const path = history.location.pathname;
		if (Util.pathIncludesTask(path)) {
			const cardPath = path.substring(Util.indexOfTask(path), path.length);
			history.push('/' + cardPath);
		} else {
			// if user doesnt have access rights to view this page redirect to no access page
			history.push('/not-authorized');
		}
		return <div></div>;
	}
	const [currentViewingDate, setCurrentViewingDate] = useState(Moment());
	const timePeriod = TIME_PERIOD.WEEK_VIEW;

	useEffect(() => {
		Util.CommitMutation(TrackPersonEventMutation, {
			name: 'team-time',
		});
		document.title = 'Team Timesheets - Forecast';
		tracking.trackPage('Team-Timesheet-page');

		const allPersonIdInt = viewer.company.allPersons.edges.map(p => parseInt(atob(p.node.id).replace('Person:', '')));
		setSocketConfig(getSocketConfig(allPersonIdInt), relay.refetch);

		dispatch(EVENT_ID.PAGE_LOADED, 'team-timesheet');
	}, []);

	const goToToday = () => {
		setCurrentViewingDate(Moment());
	};

	const adjacentPeriod = (calendareShown, forward) => {
		setCurrentViewingDate(handleAdjacentPeriodClick(currentViewingDate, forward, timePeriod));
	};

	const handleDateRange = (calendareShown, date) => {
		setCurrentViewingDate(handleDateRangeClick(date, timePeriod));
	};

	const refetch = variables => {
		relay.refetch(variables);
		setSocketVariables(variables);
	};

	return (
		<PageWrapper data-cy={'company-timesheets-page'}>
			{children}
			<TimesheetsTeamPage intl={intl} viewer={viewer} history={history} goToToday={goToToday} adjacentPeriod={adjacentPeriod} refetch={refetch} currentViewingDate={currentViewingDate} handleDateRange={handleDateRange}></TimesheetsTeamPage>
		</PageWrapper>
	);
};

const timesheetsTeamQuery = graphql`
	query timesheetsTeam_Query($searchQuery: TaskSearchQueryType, $startDateString: String, $endDateString: String) {
		viewer {
			actualPersonId
			component(name: "team-timesheet")
			...timesheetsTeam_viewer @arguments(searchQuery: $searchQuery, startDateString: $startDateString, endDateString: $endDateString)
			company {
				useTimeApproval
			}
		}
	}
`;

export {timesheetsTeamQuery};

export default withRouter(
	withSocketHandling(
		injectIntl(
			createRefetchContainer(
				timesheetsTeam,
				{
					viewer: graphql`
						fragment timesheetsTeam_viewer on Viewer @argumentDefinitions(searchQuery: {type: "TaskSearchQueryType"}, startDateString: {type: "String"}, endDateString: {type: "String"}) {
							component(name: "team-timesheet")
							id
							harvestUser
							actualPersonId
							email
							backendId
							availableFeatureFlags {
								key
							}
							filters(first: 10000, filterSection: TEAM_TIMESHEET) @connection(key: "Viewer_filters", filters: []) {
								edges {
									node {
										id
										name
										section
										value
										updatedAt
									}
								}
							}
							language
							projectOwner
							company {
								id
								harvestEnabled
								characterLimit
								lockedPeriodYear
								lockedPeriodMonth
								lockedPeriodDay
								useTimeApproval
								useTimeLocking
								modules {
									moduleType
								}
								tier
								timeRegistrations(first: 10000000, startDate: $startDateString, endDate: $endDateString, searchQuery: $searchQuery) @connection(key: "Company_timeRegistrations", filters: []) {
									edges {
										node {
											id
											day
											month
											year
											minutesRegistered
											person {
												id
											}
											project {
												id
												name
												projectColor
												companyProjectId
												fullAccessToProject
											}
											task {
												id
												name
												companyTaskId
												project {
													id
													name
													companyProjectId
													projectColor
													fullAccessToProject
												}
											}
											idleTime {
												id
												name
												isInternalTime
											}
										}
									}
								}
								teams(first: 1000000) {
									edges {
										node {
											id
											name
											teamPersons(first: 1000000) {
												edges {
													node {
														id
														person {
															id
														}
													}
												}
											}
										}
									}
								}
								roles(first: 1000000) {
									edges {
										node {
											id
											name
										}
									}
								}
								labels(first: 100000) {
									edges {
										node {
											id
											name
											color
										}
									}
								}
								departments(first: 100000) {
									edges {
										node {
											id
											name
										}
									}
								}
								allPersons(first: 1000000, excludeClientUsers: true) {
									edges {
										node {
											holidayCalendar {
												id
												holidayCalendarEntries(first: 100000) {
													edges {
														node {
															id
															day
															month
															year
															name
														}
													}
												}
												name
											}
											id
											role {
												id
												name
											}
											personLabels(first: 100000) {
												edges {
													node {
														id
														label {
															id
														}
													}
												}
											}
											department {
												id
												name
											}
											firstName
											createdAt
											startDate
											endDate
											lastName
											systemUser
											initials
											active
											monday
											tuesday
											wednesday
											thursday
											friday
											saturday
											sunday
											excludeFromCompanyLockedPeriod
											submitLockedDateYear
											submitLockedDateMonth
											submitLockedDateDay
											harvestUser
											economicUser
											profilePictureId
											profilePictureDefaultId
										}
									}
								}
							}
						}
					`,
				},
				graphql`
					query timesheetsTeamRefetchQuery($searchQuery: TaskSearchQueryType, $startDateString: String, $endDateString: String) {
						viewer {
							component(name: "team-timesheet")
							...timesheetsTeam_viewer @arguments(searchQuery: $searchQuery, startDateString: $startDateString, endDateString: $endDateString)
						}
					}
				`
			)
		)
	)
);
